import { debounce } from 'lodash';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useGetMediaKit } from '~/hooks/api/media-kits/get';
import { useUpdateMediaKit } from '~/hooks/api/media-kits/update';

import { AnyBlock } from '../types';
import { usePageBuilderStore } from './usePageBuilderStore';

/**
 * Hook to initialize page builder store with media kit data
 */
export const useInitPageBuilderStore = () => {
  const router = useRouter();
  const id = router.query.id as string;

  const mediaKit = useGetMediaKit({ id });
  const updateMediaKit = useUpdateMediaKit({ id });
  const { setBlocks, resetBlocks } = usePageBuilderStore();

  useEffect(() => {
    // Update store when media kit changes
    if (mediaKit.status !== 'success') return;
    setBlocks(mediaKit.data.blocks);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mediaKit.status]);

  useEffect(() => {
    // Reset store on unmount
    return () => {
      resetBlocks();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdates = useCallback(
    debounce((blocks: AnyBlock[]) => {
      if (!mediaKit.data?.blocks) return;
      if (!blocks.length) return;
      if (JSON.stringify(blocks) === JSON.stringify(mediaKit.data?.blocks))
        return;
      updateMediaKit.mutate({ blocks });
    }, 2000),
    [mediaKit.data],
  );

  useEffect(() => {
    // MAKE API REQUEST WHEN BLOCKS CHANGE
    const unsubscribePositionStore = usePageBuilderStore.subscribe(
      ({ blocks }) => {
        debouncedUpdates(blocks);
      },
    );

    return () => {
      unsubscribePositionStore();
    };
  }, [debouncedUpdates]);

  // Flushing debounced updates on navigation
  useEffect(() => {
    const handleRouteChange = () => {
      debouncedUpdates.flush();
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events, debouncedUpdates]);
};
